<template>
  <div>
    <DataTable :value="subTenants" :loading="loading" size="small" responsiveLayout="scroll">
      <Column field="name" header="Sub-tenant" />
      <Column field="description" header="Descripción" />
      <Column header="Usuarios" style="min-width:220px">
        <template #body="{ data }">
          <div class="user-tags">
            <Tag
              v-for="user in (subUsersMap[data.id] || [])"
              :key="`${data.id}-${user.user_id}`"
              :value="user.full_name || user.email"
              severity="success"
              size="small"
            />
            <span v-if="!(subUsersMap[data.id]?.length)" class="text-muted">Sin usuarios</span>
          </div>
        </template>
      </Column>
      <Column header="Agentes asignados" style="min-width:200px">
        <template #body="{ data }">
          <div class="agent-tags">
            <Tag
              v-for="agentId in (subAgentsMap[data.id] || [])"
              :key="agentId"
              :value="agentName(agentId)"
              severity="info"
              size="small"
            />
            <span v-if="!(subAgentsMap[data.id]?.length)" class="text-muted">Sin agentes</span>
          </div>
        </template>
      </Column>
      <Column header="" style="min-width:130px; text-align:right">
        <template #body="{ data }">
          <Button icon="pi pi-users" text rounded size="small" v-tooltip="'Ver usuarios'" @click="openUsers(data)" />
          <Button icon="pi pi-user-plus" text rounded size="small" v-tooltip="'Agregar usuario'" @click="openAddUser(data)" />
          <Button icon="pi pi-pencil" text rounded size="small" v-tooltip="'Editar sub-tenant'" @click="openEditSub(data)" />
          <Button icon="pi pi-android" text rounded size="small" v-tooltip="'Asignar agentes'" @click="openAgents(data)" />
          <Button icon="pi pi-trash" text rounded size="small" severity="danger" @click="deleteSub(data)" />
        </template>
      </Column>
      <template #empty><p class="empty-msg">No hay sub-tenants.</p></template>
    </DataTable>

    <!-- Dialog: agregar usuario al subtenant -->
    <Dialog v-model:visible="showAddUser" :header="`Agregar usuario — ${selectedSub?.name}`" modal :style="{ width: '460px' }">
      <div class="user-form">
        <div class="field">
          <label>Email <span class="req">*</span></label>
          <InputText v-model="userForm.email" type="email" class="w-full" placeholder="usuario@empresa.com" />
        </div>
        <div class="field">
          <label>Nombre completo <span class="req">*</span></label>
          <InputText v-model="userForm.full_name" class="w-full" />
        </div>
        <div class="field">
          <label>Contraseña</label>
          <PasswordField v-model="userForm.password" :auto-generate="true" />
        </div>
        <Message v-if="userFormError" severity="error" :closable="false" size="small">{{ userFormError }}</Message>
      </div>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showAddUser = false" />
        <Button label="Agregar" icon="pi pi-check" :loading="savingUser" @click="addUser" />
      </template>
    </Dialog>

    <Dialog v-model:visible="showUsers" :header="`Usuarios — ${selectedSub?.name}`" modal :style="{ width: '620px' }">
      <DataTable :value="selectedSubUsers" :loading="loadingSubUsers" size="small" responsiveLayout="scroll">
        <Column field="full_name" header="Nombre" />
        <Column field="email" header="Email" />
        <Column field="role" header="Rol">
          <template #body="{ data }">
            <Tag :value="data.role" severity="info" size="small" />
          </template>
        </Column>
        <template #empty><p class="empty-msg">No hay usuarios en este sub-tenant.</p></template>
      </DataTable>
    </Dialog>

    <Dialog v-model:visible="showEditSub" :header="`Editar sub-tenant — ${selectedSub?.name}`" modal :style="{ width: '520px' }">
      <div class="user-form">
        <div class="field">
          <label>Nombre <span class="req">*</span></label>
          <InputText v-model="editForm.name" class="w-full" />
        </div>
        <div class="field">
          <label>Descripción</label>
          <InputText v-model="editForm.description" class="w-full" />
        </div>
        <div class="field">
          <label>Tipo de entidad por defecto</label>
          <Dropdown
            v-model="editForm.default_company_type_code"
            :options="companyTypes"
            optionLabel="name"
            optionValue="code"
            showClear
            placeholder="Sin configuración"
            class="w-full"
          />
        </div>
        <div class="field">
          <label>Marco normativo por defecto</label>
          <Dropdown
            v-model="editForm.default_accounting_framework_code"
            :options="accountingFrameworks"
            optionLabel="name"
            optionValue="code"
            showClear
            placeholder="Sin configuración"
            class="w-full"
          />
        </div>
        <Message v-if="editError" severity="error" :closable="false" size="small">{{ editError }}</Message>
      </div>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showEditSub = false" />
        <Button label="Guardar" icon="pi pi-check" :loading="savingEditSub" @click="saveEditSub" />
      </template>
    </Dialog>

    <!-- Dialog: agentes del subtenant -->
    <Dialog v-model:visible="showAgents" :header="`Agentes — ${selectedSub?.name}`" modal :style="{ width: '500px' }">
      <div v-if="selectedSub" class="sub-agents">
        <p class="hint">Solo puedes asignar agentes que el tenant tenga habilitados.</p>
        <div v-for="agentId in tenantAgents" :key="agentId" class="agent-row">
          <span>{{ agentName(agentId) }}</span>
          <ToggleButton
            :modelValue="isSubEnabled(agentId)"
            onLabel="Asignado"
            offLabel="No asignado"
            onIcon="pi pi-check"
            offIcon="pi pi-times"
            @update:modelValue="toggleSubAgent(agentId, $event)"
          />
        </div>
        <p v-if="!tenantAgents.length" class="empty-msg">El tenant no tiene agentes habilitados.</p>
      </div>
    </Dialog>

    <Toast />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import ToggleButton from 'primevue/togglebutton';
import Message from 'primevue/message';
import Toast from 'primevue/toast';
import PasswordField from '../common/PasswordField.vue';
import { tenantsService } from '../../services/tenants';
import api from '../../services/api';

const props = defineProps({
  tenantId: { type: String, default: null },
  tenantAgents: { type: Array, default: () => [] },  // IDs de agentes del tenant
});

const toast = useToast();
const subTenants = ref([]);
const loading = ref(false);
const subAgentsMap = ref({});
const subUsersMap = ref({});
const allAgentsInfo = ref([]);
const companyTypes = ref([]);
const accountingFrameworks = ref([]);
const showAgents = ref(false);
const selectedSub = ref(null);
const subEnabledAgents = ref(new Set());
const showAddUser = ref(false);
const showUsers = ref(false);
const loadingSubUsers = ref(false);
const selectedSubUsers = ref([]);
const showEditSub = ref(false);
const savingEditSub = ref(false);
const editError = ref('');
const editForm = ref({
  name: '',
  description: '',
  default_company_type_code: null,
  default_accounting_framework_code: null,
});
const userForm = ref({ email: '', full_name: '', password: '' });
const userFormError = ref('');
const savingUser = ref(false);

const agentName = (id) => allAgentsInfo.value.find(a => a.id === id)?.name || `Agente ${id}`;
const isSubEnabled = (agentId) => subEnabledAgents.value.has(agentId);

const load = async () => {
  if (!props.tenantId) return;
  loading.value = true;
  try {
    subTenants.value = await tenantsService.getSubTenants(props.tenantId);
    const agentsMap = {};
    const usersMap = {};
    await Promise.all(subTenants.value.map(async (sub) => {
      try {
        const [agentsRes, usersRes] = await Promise.all([
          api.get(`/tenants/${props.tenantId}/sub-tenants/${sub.id}/agents`),
          api.get(`/tenants/${props.tenantId}/sub-tenants/${sub.id}/users`),
        ]);
        agentsMap[sub.id] = agentsRes.data.map(a => a.agent_id);
        usersMap[sub.id] = usersRes.data;
      } catch (e) {
        agentsMap[sub.id] = [];
        usersMap[sub.id] = [];
      }
    }));
    subAgentsMap.value = agentsMap;
    subUsersMap.value = usersMap;
  } catch (e) { console.error(e); }
  finally { loading.value = false; }
};

const openAgents = async (sub) => {
  selectedSub.value = sub;
  subEnabledAgents.value = new Set(subAgentsMap.value[sub.id] || []);
  showAgents.value = true;
};

const openAddUser = (sub) => {
  selectedSub.value = sub;
  userForm.value = { email: '', full_name: '', password: '' };
  userFormError.value = '';
  showAddUser.value = true;
};

const openUsers = async (sub) => {
  selectedSub.value = sub;
  loadingSubUsers.value = true;
  selectedSubUsers.value = subUsersMap.value[sub.id] || [];
  showUsers.value = true;
  try {
    const res = await api.get(`/tenants/${props.tenantId}/sub-tenants/${sub.id}/users`);
    selectedSubUsers.value = res.data;
    subUsersMap.value[sub.id] = res.data;
  } catch (e) {
    selectedSubUsers.value = [];
  } finally {
    loadingSubUsers.value = false;
  }
};

const openEditSub = (sub) => {
  selectedSub.value = sub;
  editForm.value = {
    name: sub.name || '',
    description: sub.description || '',
    default_company_type_code: sub.default_company_type_code || null,
    default_accounting_framework_code: sub.default_accounting_framework_code || null,
  };
  editError.value = '';
  showEditSub.value = true;
};

const saveEditSub = async () => {
  if (!selectedSub.value || !editForm.value.name.trim()) {
    editError.value = 'El nombre es obligatorio';
    return;
  }
  savingEditSub.value = true;
  editError.value = '';
  try {
    await tenantsService.updateSubTenant(props.tenantId, selectedSub.value.id, {
      name: editForm.value.name.trim(),
      description: editForm.value.description || null,
      default_company_type_code: editForm.value.default_company_type_code || null,
      default_accounting_framework_code: editForm.value.default_accounting_framework_code || null,
    });
    showEditSub.value = false;
    await load();
    toast.add({ severity: 'success', summary: 'Sub-tenant actualizado', life: 2500 });
  } catch (e) {
    editError.value = e.response?.data?.detail || e.message;
  } finally {
    savingEditSub.value = false;
  }
};

const addUser = async () => {
  if (!userForm.value.email || !userForm.value.full_name) {
    userFormError.value = 'Email y nombre son obligatorios';
    return;
  }
  savingUser.value = true; userFormError.value = '';
  try {
    await api.post(`/tenants/${props.tenantId}/sub-tenants/${selectedSub.value.id}/users`, userForm.value);
    showAddUser.value = false;
    await load();
    toast.add({ severity: 'success', summary: 'Usuario agregado al sub-tenant', life: 2500 });
  } catch (e) {
    userFormError.value = e.response?.data?.detail || e.message;
  } finally {
    savingUser.value = false;
  }
};

const toggleSubAgent = async (agentId, enable) => {
  if (!selectedSub.value) return;
  try {
    if (enable) {
      await api.post(`/tenants/${props.tenantId}/sub-tenants/${selectedSub.value.id}/agents`, { agent_id: agentId });
      subEnabledAgents.value.add(agentId);
    } else {
      await api.delete(`/tenants/${props.tenantId}/sub-tenants/${selectedSub.value.id}/agents/${agentId}`);
      subEnabledAgents.value.delete(agentId);
    }
    subAgentsMap.value[selectedSub.value.id] = [...subEnabledAgents.value];
    toast.add({ severity: 'success', summary: enable ? 'Agente asignado' : 'Agente removido', life: 2000 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 3000 });
  }
};

const deleteSub = async (sub) => {
  try {
    await tenantsService.deleteSubTenant(props.tenantId, sub.id);
    await load();
    toast.add({ severity: 'success', summary: 'Sub-tenant eliminado', life: 2500 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail, life: 3000 });
  }
};

onMounted(async () => {
  await load();
  try {
    const [agentsRes, companyTypesRes, frameworksRes] = await Promise.all([
      api.get('/agents/'),
      api.get('/catalogs/company-types'),
      api.get('/catalogs/accounting-frameworks'),
    ]);
    allAgentsInfo.value = agentsRes.data;
    companyTypes.value = companyTypesRes.data;
    accountingFrameworks.value = frameworksRes.data;
  } catch (e) { console.error(e); }
});

watch(() => props.tenantId, load);
</script>

<style scoped>
.agent-tags { display: flex; flex-wrap: wrap; gap: 0.3rem; }
.user-tags { display: flex; flex-wrap: wrap; gap: 0.3rem; }
.text-muted { color: #9ca3af; font-size: 0.8rem; }
.empty-msg { text-align: center; color: #9ca3af; padding: 0.75rem; }
.sub-agents { display: flex; flex-direction: column; gap: 0.5rem; }
.hint { font-size: 0.8rem; color: #6b7280; margin: 0; }
.agent-row { display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; border-radius: 6px; }

.user-form { display: flex; flex-direction: column; gap: 0.75rem; }
.field { display: flex; flex-direction: column; gap: 0.3rem; }
.field label { font-weight: 600; font-size: 0.82rem; color: #374151; }
.req { color: #ef4444; }
.w-full { width: 100%; }
</style>
